import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import API_ENDPOINTS from '../../config/api';
import { useInstanceId } from '../../contexts/InstanceContext';
import { useDashboardData } from '../useDashboardData';
import { TileHint } from '../TileFrame';
import { DASH_COLORS, DASH_TYPE } from '../dashboardTokens';
import CorpusTietopohjaV4Screen from '../../screens/CorpusTietopohjaV4Screen';

// RAG source map tile (#1159, specs/workshop-dashboard.md): the #629 corpus
// as a mini bubble map — every source a dot, usage-sized, cluster-colored,
// unretrieved ones dim — plus the sources/retrieved counter. New rendering
// code over the same precomputed v4 layout; CorpusTietopohjaV4Screen stays
// as is (redesign rule). 20 s refresh, the cadence proven on the v4 screen.

const REFRESH_MS = 20000;

function useRagMap(instanceId) {
  const [map, setMap] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!instanceId) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.RAG_SOURCE_MAP_V4(instanceId), { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = await res.json();
        if (alive) { setMap(payload); setError(null); }
      } catch (err) {
        if (alive) setError(err.message || String(err));
      }
    };
    load();
    const poll = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(poll); };
  }, [instanceId]);
  return { map, error };
}

const MapSvg = styled.svg`
  flex: 1;
  width: 100%;
  min-height: 0;
`;

const Counter = styled.div`
  font-size: ${DASH_TYPE.meta};
  color: ${DASH_COLORS.textDim};
  margin-top: 0.6vmin;
`;

function viewBoxOf(nodes) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  for (const n of nodes) {
    const r = Math.max(n.modeARadius || 0, n.modeBRadius || 0);
    minX = Math.min(minX, n.x - r); maxX = Math.max(maxX, n.x + r);
    minY = Math.min(minY, n.y - r); maxY = Math.max(maxY, n.y + r);
  }
  const pad = 8;
  return `${minX - pad} ${minY - pad} ${maxX - minX + 2 * pad} ${maxY - minY + 2 * pad}`;
}

function Bubbles({ map }) {
  const viewBox = useMemo(() => viewBoxOf(map.nodes), [map.nodes]);
  return (
    <MapSvg viewBox={viewBox} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      {map.nodes.map((n) => (
        <circle
          key={n.id}
          cx={n.x}
          cy={n.y}
          r={n.retrieved ? n.modeBRadius : Math.max(2, (n.modeARadius || 4) * 0.5)}
          fill={n.retrieved ? (map.clusterColors?.[n.cluster] || DASH_COLORS.accent) : 'none'}
          stroke={map.clusterColors?.[n.cluster] || DASH_COLORS.textDim}
          strokeWidth={n.retrieved ? 0 : 1}
          strokeDasharray={n.retrieved ? undefined : '2 2'}
          opacity={n.retrieved ? 0.95 : 0.45}
        />
      ))}
    </MapSvg>
  );
}

function RagCounter({ t }) {
  const { data } = useDashboardData();
  const rag = data?.rag;
  if (!rag) return null;
  return (
    <Counter>
      {t('dashboard.ragCounter', { total: rag.totalSources, retrieved: rag.retrievedSources })}
    </Counter>
  );
}

export function RagTileBody({ t }) {
  const instanceId = useInstanceId();
  const { map, error } = useRagMap(instanceId);
  if (error) return <TileHint>{t('dashboard.stale')}</TileHint>;
  if (!map?.nodes?.length) return <TileHint>{t('dashboard.noData')}</TileHint>;
  return (
    <>
      <Bubbles map={map} />
      <RagCounter t={t} />
    </>
  );
}

// The overlay embeds the interactive corpus visualizer itself (decision
// 2026-10-19), without the workspace chrome — no Kuutio name, no instance
// picker. The inner wrapper gives the d3 canvas a real height to measure.
export function RagTileDetail() {
  return (
    <div style={{ height: '100%', minHeight: '70vh' }}>
      <CorpusTietopohjaV4Screen embedded />
    </div>
  );
}
